import { SDK_ERRORS, SDK_LIMITS } from '@riz/contracts/sdk';
import { rejectWith, rizError } from '../core/errors.ts';
import type { SdkClient } from '../core/client.ts';

export interface DataModule {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
  /** Bytes used and the ceiling, so a game can warn before it fails. */
  usage(): Promise<{ used: number; limit: number }>;
  flush(): Promise<void>;
}

/**
 * Shaped like `localStorage` so porting a game is a find-and-replace, but
 * account-scoped and synced: a player who signs in on another device finds
 * their progress, and a guest's data follows them into an account.
 *
 * Writes are debounced and coalesced per key. A game that saves on every frame
 * would otherwise spend its rate limit on storage.
 */
export function createDataModule(client: SdkClient): DataModule {
  const cache = new Map<string, string>();
  const dirty = new Map<string, string | null>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> | null = null;

  async function commit(): Promise<void> {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (!dirty.size) return;

    const batch = [...dirty.entries()];
    dirty.clear();
    const writes = batch.filter(([, value]) => value !== null) as Array<[string, string]>;
    const removals = batch.filter(([, value]) => value === null).map(([key]) => key);

    for (const key of removals) {
      await client.call('command', 'data/remove', { key });
    }
    for (const [key, value] of writes) {
      await client.call('command', 'data/set', { key, value });
    }
  }

  function schedule(): Promise<void> {
    if (timer) clearTimeout(timer);
    inFlight = new Promise<void>((resolve, reject) => {
      timer = setTimeout(() => {
        commit().then(resolve, reject);
      }, SDK_LIMITS.dataWriteDebounceMs);
    });
    return inFlight;
  }

  function projectedBytes(key: string, value: string): number {
    let total = key.length + value.length;
    for (const [existingKey, existingValue] of cache) {
      if (existingKey === key) continue;
      total += existingKey.length + existingValue.length;
    }
    return total;
  }

  return {
    async getItem(key) {
      const capability = client.requireCapability<string | null>('data.storage', 'Progress save');
      if (capability) return capability;
      if (cache.has(key)) return cache.get(key) ?? null;

      const result = await client.call('command', 'data/get', { key });
      const value = typeof result.value === 'string' ? result.value : null;
      if (value !== null) cache.set(key, value);
      return value;
    },

    async setItem(key, value) {
      const capability = client.requireCapability<void>('data.storage', 'Progress save');
      if (capability) return capability;
      if (typeof key !== 'string' || !key) {
        return rejectWith(SDK_ERRORS.invalidPayload, 'data.setItem needs a non-empty key.');
      }
      const serialised = String(value);
      const projected = projectedBytes(key, serialised);
      if (projected > SDK_LIMITS.maxDataBytes) {
        throw rizError(
          SDK_ERRORS.dataLimitExceeded,
          `Saved data cannot exceed ${SDK_LIMITS.maxDataBytes} bytes.`,
          { limit: SDK_LIMITS.maxDataBytes, projected },
        );
      }
      cache.set(key, serialised);
      dirty.set(key, serialised);
      await schedule();
    },

    async removeItem(key) {
      const capability = client.requireCapability<void>('data.storage', 'Progress save');
      if (capability) return capability;
      cache.delete(key);
      dirty.set(key, null);
      await schedule();
    },

    async clear() {
      const capability = client.requireCapability<void>('data.storage', 'Progress save');
      if (capability) return capability;
      cache.clear();
      dirty.clear();
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      await client.call('command', 'data/clear', {});
    },

    async usage() {
      const capability = client.requireCapability<{ used: number; limit: number }>('data.storage', 'Progress save');
      if (capability) return capability;
      let used = 0;
      for (const [key, value] of cache) used += key.length + value.length;
      return { used, limit: SDK_LIMITS.maxDataBytes };
    },

    async flush() {
      await commit();
      if (inFlight) await inFlight.catch(() => {});
    },
  };
}
